import React, { useEffect, useRef, useState } from "react";

const VIEWPORT_PRELOAD_MARGIN = "320px 0px";

function shouldLoadImmediately(eager = false) {
  return (
    eager ||
    typeof window === "undefined" ||
    typeof window.IntersectionObserver === "undefined"
  );
}

function LazyImage({ alt, className, eager = false, src, title }) {
  const imageRef = useRef(null);
  const [isVisible, setIsVisible] = useState(() => shouldLoadImmediately(eager));
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(false);
    setIsVisible(shouldLoadImmediately(eager));
  }, [eager, src]);

  useEffect(() => {
    const node = imageRef.current;

    if (isVisible && node?.complete && node.currentSrc) {
      setIsLoaded(true);
    }
  }, [isVisible, src]);

  useEffect(() => {
    if (isVisible) {
      return undefined;
    }

    const node = imageRef.current;
    if (!node || typeof window.IntersectionObserver === "undefined") {
      setIsVisible(true);
      return undefined;
    }

    const observer = new window.IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry && (entry.isIntersecting || entry.intersectionRatio > 0)) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: VIEWPORT_PRELOAD_MARGIN,
      }
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [isVisible]);

  return (
    <img
      alt={alt}
      className={className}
      data-loaded={isLoaded ? "true" : "false"}
      decoding="async"
      fetchpriority={eager ? "high" : "auto"}
      loading={eager ? "eager" : "lazy"}
      onLoad={() => setIsLoaded(true)}
      ref={imageRef}
      src={isVisible ? src : undefined}
      title={title}
    />
  );
}

export default LazyImage;
